import { confirmScriptUpload, requestScriptUpload, type AssetResponse } from '../../api';
import { formatSize, type RejectedFile } from './constants';
import { stemOf } from './pairSidecars';
import { putFileToSignedUrl, type PutFile } from './putFileToSignedUrl';

/**
 * A film's script: the .xlsx it was cut to, kept beside it.
 *
 * The browser checks only what it can see — the extension and the size — so a
 * wrong file is named before anything uploads. The backend checks the bytes
 * on confirm (`core/script.py`) and is the one that refuses a macro-enabled
 * workbook renamed to .xlsx.
 */

export const SCRIPT_ACCEPT =
  '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Mirrors `MAX_SCRIPT_BYTES` in `apps/asset-library/backend/src/core/script.py`. */
export const MAX_SCRIPT_BYTES = 10 * 1024 * 1024;

export const isScriptFile = (file: File) => file.name.toLowerCase().endsWith('.xlsx');

/** Why *file* cannot be a script, in the backend's words, or null if it can. */
export function scriptProblem(file: File): string | null {
  if (!isScriptFile(file)) {
    const dot = file.name.lastIndexOf('.');
    const extension = dot > 0 ? file.name.slice(dot).toLowerCase() : 'without an extension';
    return `Scripts must be .xlsx; this file is ${extension}.`;
  }
  if (file.size === 0) return 'File is empty';
  if (file.size > MAX_SCRIPT_BYTES) {
    return `Script is ${formatSize(file.size)}; the limit is ${formatSize(MAX_SCRIPT_BYTES)}.`;
  }
  return null;
}

export interface ScriptPairing {
  /** Film key → its script. */
  paired: Map<string, File>;
  rejected: RejectedFile[];
}

/**
 * Match dropped scripts to queued films by name: `hero_30s.xlsx` belongs to
 * `hero_30s.mp4`. Case-insensitive, because the same agency exports one name
 * from two tools.
 *
 * A script with no film of its name is refused by name rather than guessed
 * at, and so is one whose name two films share: either way the uploader can
 * attach it by hand from the film's row.
 */
export function pairScripts(
  films: { key: string; file: File }[],
  scripts: File[],
): ScriptPairing {
  const byStem = new Map<string, string[]>();
  for (const { key, file } of films) {
    const stem = stemOf(file.name).toLowerCase();
    byStem.set(stem, [...(byStem.get(stem) ?? []), key]);
  }

  const paired = new Map<string, File>();
  const rejected: RejectedFile[] = [];
  for (const script of scripts) {
    const problem = scriptProblem(script);
    if (problem) {
      rejected.push({ filename: script.name, reason: problem });
      continue;
    }
    const keys = byStem.get(stemOf(script.name).toLowerCase()) ?? [];
    if (keys.length === 1) {
      paired.set(keys[0], script);
    } else {
      rejected.push({
        filename: script.name,
        reason:
          keys.length === 0
            ? `No film named ${stemOf(script.name)} in this upload — attach it from the film’s row`
            : `${keys.length} films are named ${stemOf(script.name)} — attach it from the right film’s row`,
      });
    }
  }
  return { paired, rejected };
}

/** Injected so tests can drive the sequence without a network. */
export interface ScriptUploadDeps {
  requestScriptUpload: typeof requestScriptUpload;
  confirmScriptUpload: typeof confirmScriptUpload;
  putFile: PutFile;
}

/**
 * Ask, PUT, confirm: make *file* the script of the staged film *assetId*.
 * Rejects with the failing request's error; nothing changes on the film
 * unless the confirm succeeds.
 */
export async function uploadScript(
  assetId: string,
  file: File,
  { signal, deps: overrides }: { signal?: AbortSignal; deps?: Partial<ScriptUploadDeps> } = {},
): Promise<AssetResponse> {
  // Built per call, not at module level, so nothing reads the API module's
  // exports while this one loads; tests that mock part of it can still
  // import the overlay.
  const deps: ScriptUploadDeps = {
    requestScriptUpload,
    confirmScriptUpload,
    putFile: putFileToSignedUrl,
    ...overrides,
  };
  const target = await deps.requestScriptUpload(assetId, file.name, signal);
  await deps.putFile(target.upload_url, file, target.content_type, {
    signal,
    onProgress: () => {},
  });
  return deps.confirmScriptUpload(assetId, target.upload_id, file.name, signal);
}
